// Fixed 390×760 phone canvas scaled to fill its container via CSS `zoom`
// (ref parity — MobilePreviewFrame.tsx:21-45 in oa-studio-prototype). Content is
// designed for a 390-wide phone, so scaling the whole canvas keeps every element
// in the exact ref proportion at any size (full preview OR tiny strip thumbnail).
// `zoom` (not `transform`) avoids stacking-context issues with Radix popovers.
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { PhoneScaleContext } from './phoneScale'

import { VIEWPORTS, type PreviewViewport } from './previewViewports'

export const PhoneCanvas = ({
  children,
  className,
  viewport = 'phone',
  fit = 'contain',
}: {
  children: ReactNode
  className?: string
  /** Какой экран рисуем (`previewViewports.ts`); по умолчанию телефон. */
  viewport?: PreviewViewport
  /**
   * `width` — точно по ширине контейнера через `transform` (миниатюры ленты):
   * `zoom` округляет размер до пикселя, и между холстом и язычком/краем
   * карточки оставались щели в долю пикселя. Поповеров в миниатюре нет, так
   * что подвох `transform` (своя система координат) ей не страшен.
   */
  fit?: 'contain' | 'width'
}) => {
  const { width: baseWidth, height: baseHeight } = VIEWPORTS[viewport]
  const ref = useRef<HTMLDivElement>(null)
  // `null` — размер ещё не измерен. Холст до замера не показываем, а меряем в
  // `useLayoutEffect` (до отрисовки кадра): иначе на смене урока миниатюра на кадр
  // вспыхивала в полный размер 390×760 и растягивала ленту.
  const [scale, setScale] = useState<number | null>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => {
      const s =
        fit === 'width'
          ? el.getBoundingClientRect().width / baseWidth
          : Math.min(el.clientWidth / baseWidth, el.clientHeight / baseHeight)
      setScale(s > 0 ? s : 1)
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [baseWidth, baseHeight, fit])

  return (
    <div
      ref={ref}
      className={cn(
        'h-full w-full overflow-hidden',
        fit === 'width' ? 'relative' : 'flex items-center justify-center',
        className,
      )}
    >
      <div
        className={cn(
          // Внутри экрана урока полос прокрутки нет нигде: в плеере (телефон) их не
          // видно, а в Windows они ещё и отъедали ширину и сдвигали вёрстку.
          'shrink-0 overflow-hidden [&_*]:[scrollbar-width:none] [&_*::-webkit-scrollbar]:hidden',
          fit === 'width' ? 'absolute left-0 top-0 origin-top-left' : 'relative',
        )}
        style={{
          width: baseWidth,
          height: baseHeight,
          ...(fit === 'width' ? { transform: `scale(${scale ?? 1})` } : { zoom: scale ?? 1 }),
          visibility: scale === null ? 'hidden' : undefined,
          // Без своей заливки: фон у каждого экрана свой (`PreviewShell` style), а
          // белая подложка на дробном масштабе выглядывала по краям тёмного экрана
          // (видео) белыми пикселями.
        }}
      >
        <PhoneScaleContext.Provider value={scale ?? 1}>{children}</PhoneScaleContext.Provider>
      </div>
    </div>
  )
}
